import {
  MappedField,
  SortColumn,
} from 'libs/datagrid/src/lib/containers/dx-datagrid/dx-datagrid.types';
import { hasAllowedFields } from './field-structure.utils';

export type PortfolioFields = {
  /**
   * Required for commit form
   */
  controlType?: PortfolioPlaceHolderTypeVal;
  /**
   * Shows dataType tah
   */
  dataType?: PortfolioPlaceHolderTypeVal;
  editType?: string;
  /**
   * Shows if field should be excluded in specific forms
   */
  exclude?: PortfolioPlaceHolderTypeVal;
  exclusive?: string;
  format?: string;
  /**
   * Shows the name that should be displayed in forms
   */
  headerText?: PortfolioPlaceHolderTypeVal;

  /**
   * Used as field in forms
   */
  id: PortfolioPlaceHolderTypeVal;
  /**
   * (Datagrid)
   */
  isPrimaryKey?: boolean;
  /**
   * Shows if the field is mandatory in forms
   */
  isRequired?: PortfolioPlaceHolderTypeVal;
  /**
   * Shows if it should be visible by default in forms
   */
  visible?: PortfolioPlaceHolderTypeVal;
};

export type PortfolioPlaceHolderTypeVal = {
  bulkEditForm?: boolean | number | string;
  createForm?: boolean | number | string;
  datagrid?: boolean | number | string;
  editForm?: boolean | number | string;
  nestedCreateAdditionForm?: boolean | number | string;
  nestedEditAdditionForm?: boolean | number | string;
};

export enum EnumPortfolioViews {
  EDIT = 0,
  ADDITIONS = 1,
  PN_INFO = 2,
  VC_INFO = 3,
}

export enum EnumPortfolioFieldStructure {
  PortfolioTable = 0,
  PortfolioCreateForm = 1,
  PortfolioEditForm = 2,
  BulkEditForm = 3,
  NestedAdditionTable = 4,
  NestedCreateAdditionForm = 5,
  NestedEditAdditionForm = 6,
}

export interface PortfolioFilterUIModel {
  additionField?: string;
  additionFieldValue?: string;
  contact?: boolean;
  contacts?: string | string[];
  contactType?: string;
  dateFrom?: Date;
  dateTo?: Date;
  dateType?: string;
  flags?: string | string[];
  materialGroups?: string | string[];
  partNumbers?: string | string[];
  sorts?: SortColumn[];
  vendorCodes?: string | string[];
}

export type TransformedIdentity = {
  [key: string]: string | undefined;
  email?: string;
  identitykey?: string;
  name?: string;
};

export interface VendorName {
  vendorCode: string;
  vendorName: string;
}

export interface VendorsDetailDropdown {
  vendorCode: string;
  vendorCodeName: string;
}

export type PnVcAdditionInfo = {
  partNumber: string;
  propertyName: string;
  vendorCode: string;
};

export interface PnVcAdditions {
  partNumber: string;
  vendorCode: string;
}

export interface BatchEditPortfolios {
  fields: Array<{ key: string; value: boolean | Date | number | string }>;
  ids: string[];
}

export type PortfolioProperty = {
  [key: string]: unknown;
  id: string;
  name: string;
  type:
    | 'boolean'
    | 'custom'
    | 'd-identityKey'
    | 'date'
    | 'dropdown'
    | 'dynamic_field'
    | 'number'
    | 'string';
};

export interface CreateOrUpdatePnVcAdditions {
  partNumber: string;
  propertyName?: string;
  value?: string;
  vendorCode: string;
}
export interface Portfolio {
  [key: string]: unknown;
  contactType?: string;
  email?: string;
  firstName?: string;
  flags?: string[];
  id: string;
  identityKey?: string;
  lastName?: string;
  partNumber?: string;
  validFrom?: string;
  validTo?: string;
  vendorCode?: string;
}

export class PortfolioVisualization {
  pageSettings!: { pageIndex: number; pageSize: number };
  columnSort: null | SortColumn[] = null;
}

export class PortfolioListField {
  fields: MappedField[];
  userRightFields!: string[];
  hasField: (
    field: PortfolioFields,
    refId: keyof PortfolioPlaceHolderTypeVal,
    actualId: keyof PortfolioPlaceHolderTypeVal,
  ) => boolean;

  constructor(type?: EnumPortfolioFieldStructure, userRightFields?: string[]) {
    if (hasAllowedFields(userRightFields)) {
      this.userRightFields = userRightFields.map((e): string => e.toLowerCase());
      this.hasField = this.validFunWithUserFields;
    } else {
      this.hasField = this.validFunWithoutUserFields;
    }
    this.fields = this.getDefaultFields(type);
  }

  getDefaultFields(type?: EnumPortfolioFieldStructure): MappedField[] {
    switch (type) {
      case EnumPortfolioFieldStructure.PortfolioTable:
        return this.getPortfolioFields();
      case EnumPortfolioFieldStructure.PortfolioEditForm:
        return this.getEditPortfolioFormFields();
      case EnumPortfolioFieldStructure.PortfolioCreateForm:
        return this.getCreatePortfolioFormFields();
      case EnumPortfolioFieldStructure.BulkEditForm:
        return this.getBulkFormFields();
      case EnumPortfolioFieldStructure.NestedAdditionTable:
        return this.getPnVcAdditionsFields();
      case EnumPortfolioFieldStructure.NestedCreateAdditionForm:
        return this.getPnVcAdditionFormFields();
      case EnumPortfolioFieldStructure.NestedEditAdditionForm:
        return this.getPnVcEditAdditionFormFields();
      default:
        return this.getPortfolioFields();
    }
  }

  /**
   *
   * @param field field which inclusiveness needs to check
   * @param refId Id
   * @param actualId actual Id on which fields belongs
   * @returns is valid field
   */
  validFunWithUserFields = (
    field: PortfolioFields,
    refId: keyof PortfolioPlaceHolderTypeVal,
    actualId: keyof PortfolioPlaceHolderTypeVal,
  ): boolean => {
    if (
      this.userRightFields &&
      !(field.isRequired && field.isRequired[actualId]) //For adding fields not present in default fields
    ) {
      // return this.userRightFields.includes((<string>field.id[refId]).toLowerCase());
      return (
        this.userRightFields.includes((<string>field.id[refId]).toLowerCase()) &&
        !(field.exclude && field.exclude[actualId] == true)
      ); //2571 Comment
    } else {
      return this.validFunWithoutUserFields(field, refId, actualId);
    }
  };

  validFunWithoutUserFields = (
    field: PortfolioFields,
    refId: keyof PortfolioPlaceHolderTypeVal,
    actualId: keyof PortfolioPlaceHolderTypeVal,
  ): boolean => {
    if (field.exclude && field.exclude[actualId] == true) {
      return false;
    } else {
      return true;
    }
  };

  //#region PORTFOLIO (for DataGrid & CreateForm & EditForm)

  getPortfolioFields(): MappedField[] {
    return this.portfolioField
      .filter((field: PortfolioFields): boolean => {
        return this.hasField(field, 'datagrid', 'datagrid');
      })
      .map((field: PortfolioFields): MappedField => {
        return {
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          type: field.dataType?.datagrid ? (field.dataType.datagrid as string) : 'string',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  getEditPortfolioFormFields(): MappedField[] {
    return this.portfolioField
      .filter((field: PortfolioFields): boolean => {
        return this.hasField(field, 'datagrid', 'editForm');
      })
      .map((field: PortfolioFields): MappedField => {
        return {
          controlType: field.controlType?.editForm ? (field.controlType.editForm as string) : '',
          field: (field.id?.datagrid as string) ?? '',
          headerText:
            (field.headerText?.editForm as string) ?? (field.headerText?.datagrid as string) ?? '',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  getCreatePortfolioFormFields(): MappedField[] {
    return this.portfolioField
      .filter((field: PortfolioFields): boolean => {
        return this.hasField(field, 'datagrid', 'createForm');
      })
      .map((field: PortfolioFields): MappedField => {
        return {
          controlType: field.controlType?.createForm
            ? (field.controlType.createForm as string)
            : '',
          field: (field.id?.datagrid as string) ?? '',
          headerText:
            (field.headerText?.createForm as string) ??
            (field.headerText?.datagrid as string) ??
            '',
          isRequired: !!field.isRequired?.createForm,
          visible: field.visible?.createForm
            ? !!field.visible.createForm
            : !!field.visible?.datagrid,
        };
      });
  }

  createDefaultField(field: string): PortfolioFields {
    return {
      dataType: {
        bulkEditForm: 'text',
      },
      headerText: {
        bulkEditForm: field,
      },
      id: { bulkEditForm: field, datagrid: field },
    };
  }

  getBulkFormFields(): MappedField[] {
    // The bulk-edit dialog template + `addNewProperties` read `.id`, `.name`
    // and `.type` (see `portfolio-bulk-edit.component.{html,ts}`). The Angular
    // 20 migration accidentally repointed this method to emit `MappedField`'s
    // `field` / `headerText` keys, which made every option label resolve to
    // `'portfolio.' + undefined` → `portfolio.undefined`. We carry both
    // shapes on each entry: legacy `id`/`name` consumed by the bulk-edit
    // dialog, plus `field`/`headerText`/`visible` to satisfy the declared
    // `MappedField[]` return type used by `getDefaultFields` and downstream
    // callers (portfolio-list, portfolio-additions).
    const ccFields = this.portfolioField.slice();
    return ccFields
      .filter((field: PortfolioFields): boolean => {
        if (field.id['bulkEditForm']) {
          return this.userRightFields.includes((<string>field.id['bulkEditForm']).toLowerCase());
        }
        return false;
      })
      .map((field: PortfolioFields): MappedField => {
        const key = (field.id?.bulkEditForm as string) ?? '';
        const header =
          (field.headerText?.bulkEditForm as string) ??
          (field.headerText?.datagrid as string) ??
          '';
        const type = field.dataType?.bulkEditForm
          ? (field.dataType.bulkEditForm as string)
          : 'string';
        return {
          field: key,
          headerText: header,
          id: key,
          name: header,
          type,
          visible: true,
        } as MappedField;
      });
  }

  public readonly portfolioField: PortfolioFields[] = [
    {
      dataType: { datagrid: 'actions' },
      exclude: { bulkEditForm: true },
      headerText: { datagrid: 'Actions' },
      id: { datagrid: 'actions' },
      isRequired: { datagrid: true },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      exclude: { createForm: true },
      headerText: { datagrid: 'ID' },
      id: { datagrid: 'id' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      exclude: { createForm: true, editForm: true },
      headerText: { datagrid: 'Plant' },
      id: { datagrid: 'plant' },
      visible: { datagrid: true },
    },
    {
      controlType: { createForm: 'd-partNumber' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Part Number' },
      id: { datagrid: 'partNumber' },
      isRequired: { createForm: true, datagrid: true },
      visible: { datagrid: true },
    },
    {
      controlType: { createForm: 'd-vendorCode', editForm: 'd-vendorCode' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Vendor Code' },
      id: { datagrid: 'vendorCode' },
      isRequired: { createForm: true },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'checkbox' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Addition Field' },
      id: { datagrid: 'hasAdditionField' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'flag' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Flags' },
      id: { datagrid: 'flags' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Description' },
      id: { datagrid: 'description' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Material Group' },
      id: { datagrid: 'materialGroup' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      exclude: { createForm: true },
      headerText: { datagrid: 'HS Code' },
      id: { datagrid: 'commodityCode' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Manufacturer PN' },
      id: { datagrid: 'manufacturerPartNumber' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Vendor Manufacturer PN' },
      id: { datagrid: 'vendorManufacturerPartNumber' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      exclude: { createForm: true },
      headerText: { datagrid: 'AWS PN' },
      id: { datagrid: 'awsPartNumber' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      exclude: { createForm: true },
      headerText: { datagrid: 'MM PN' },
      id: { datagrid: 'mmPartNumber' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      exclude: { createForm: true },
      headerText: { datagrid: 'MRP Controller' },
      id: { datagrid: 'mrpController' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Profit Center' },
      id: { datagrid: 'profiCenter' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Part Class' },
      id: { datagrid: 'partClass' },
      visible: { datagrid: true },
    },
    {
      controlType: { createForm: 'd-identityKey' },
      dataType: { datagrid: 'string' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Vendor Email' },
      id: { datagrid: 'vendorEmail' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Vendor Name' },
      id: { datagrid: 'vendorName' },
      visible: { datagrid: true },
    },
    {
      controlType: { createForm: 'd-identityKey', editForm: 'd-identityKey' },
      dataType: { bulkEditForm: 'd-identityKey', datagrid: 'string' },
      headerText: {
        bulkEditForm: 'Buyer Email',
        createForm: 'Buyer Email',
        datagrid: 'Buyer Email',
        editForm: 'Buyer Email',
      },
      id: { bulkEditForm: 'buyerEmail', datagrid: 'buyerEmail' },
      // Required when creating a portfolio. Develop marks it by appending a
      // literal asterisk to the label and validates nothing; here the flag
      // drives both the marker and the validator, so the form cannot be
      // submitted without it.
      isRequired: { createForm: true },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Buyer Name' },
      id: { datagrid: 'buyerName' },
      visible: { datagrid: true },
    },
    {
      controlType: { createForm: 'd-identityKey', editForm: 'd-identityKey' },
      dataType: { bulkEditForm: 'd-identityKey', datagrid: 'string' },
      headerText: {
        bulkEditForm: 'Customer Email',
        createForm: 'Customer Email',
        datagrid: 'Customer Email',
        editForm: 'Customer Email',
      },
      id: { bulkEditForm: 'customerEmail', datagrid: 'customerEmail' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Customer Name' },
      id: { datagrid: 'customerName' },
      visible: { datagrid: true },
    },
    {
      controlType: { createForm: 'd-identityKey', editForm: 'd-identityKey' },
      dataType: { bulkEditForm: 'd-identityKey', datagrid: 'string' },
      headerText: {
        bulkEditForm: 'WHBuyer Email',
        createForm: 'WHBuyer Email',
        datagrid: 'WHBuyer Email',
        editForm: 'WHBuyer Email',
      },
      id: { bulkEditForm: 'whBuyerEmail', datagrid: 'whBuyerEmail' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      exclude: { createForm: true },
      headerText: { datagrid: 'WhBuyer Name' },
      id: { datagrid: 'whBuyerName' },
      visible: { datagrid: true },
    },
    {
      controlType: { createForm: 'd-identityKey', editForm: 'd-identityKey' },
      dataType: { bulkEditForm: 'd-identityKey', datagrid: 'string' },
      headerText: {
        bulkEditForm: 'Supplier Email',
        createForm: 'Supplier Email',
        datagrid: 'Supplier Email',
        editForm: 'Supplier Email',
      },
      id: { bulkEditForm: 'supplierEmail', datagrid: 'supplierEmail' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Supplier Name' },
      id: { datagrid: 'supplierName' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Vendor Street' },
      id: { datagrid: 'vendorStreet' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Vendor City' },
      id: { datagrid: 'vendorCity' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Vendor Postcode' },
      id: { datagrid: 'vendorPostCode' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Vendor State' },
      id: { datagrid: 'vendorState' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Vendor Country Code' },
      id: { datagrid: 'vendorCountryCode' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Last Modify By' },
      id: { datagrid: 'lastModifyBy' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Last Modify Date' },
      id: { datagrid: 'lastModifyDate' },
      visible: { datagrid: true },
    },
  ];

  //#endregion PORTFOLIO

  //#region PORTFOLIO-ADDITIONS (for DataGrid and Form)

  getPnVcAdditionsFields(): MappedField[] {
    return this.pnVcAdditionFields
      .filter((field: PortfolioFields): boolean => {
        return this.hasField(field, 'datagrid', 'datagrid');
      })
      .map((field: PortfolioFields): MappedField => {
        return {
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          type: field.dataType?.datagrid ? (field.dataType.datagrid as string) : 'string',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  getPnVcAdditionFormFields(): MappedField[] {
    return this.pnVcAdditionFields
      .filter((field: PortfolioFields): boolean => {
        return this.hasField(field, 'datagrid', 'nestedCreateAdditionForm');
      })
      .map((field: PortfolioFields): MappedField => {
        return {
          controlType: field.controlType?.nestedCreateAdditionForm
            ? (field.controlType.nestedCreateAdditionForm as string)
            : '',
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  getPnVcEditAdditionFormFields(): MappedField[] {
    return this.pnVcAdditionFields
      .filter((field: PortfolioFields): boolean => {
        return this.hasField(field, 'datagrid', 'nestedEditAdditionForm');
      })
      .map((field: PortfolioFields): MappedField => {
        return {
          controlType: field.controlType?.nestedEditAdditionForm
            ? (field.controlType.nestedEditAdditionForm as string)
            : '',
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  public readonly pnVcAdditionFields: PortfolioFields[] = [
    {
      dataType: { datagrid: 'actions' },
      headerText: { datagrid: 'Actions' },
      id: { datagrid: 'actions' },
      visible: { datagrid: true },
    },
    {
      controlType: { nestedCreateAdditionForm: 'simple' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Part Number' },
      id: { datagrid: 'partNumber' },
      visible: { datagrid: false },
    },
    {
      controlType: { nestedCreateAdditionForm: 'simple' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Vendor Code' },
      id: { datagrid: 'vendorCode' },
      visible: { datagrid: false },
    },
    {
      controlType: { nestedCreateAdditionForm: 'simple', nestedEditAdditionForm: 'simple' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Property Name' },
      id: { datagrid: 'propertyName' },
      visible: { datagrid: true },
    },
    {
      controlType: { nestedCreateAdditionForm: 'simple', nestedEditAdditionForm: 'simple' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Value' },
      id: { datagrid: 'value' },
      visible: { datagrid: true },
    },
  ];

  //#endregion PORTFOLIO-ADDITIONS
}
